// Kitchen routes (Carl, Oct 2026: "our dishes go through 4 stations: chicken, fry, build and
// pass"). The same rules as backend/kitchen.py `section_routes` / `route_for`, for the back
// office that sets routes up and the kitchen screens that show them. A route is the stations
// a dish goes through in order; the pass is never in it, because every dish ends there.

/** As backend/kitchen.py `category_key`: "Beer & cider" → "beer_and_cider". */
export const categoryKey = (category) => String(category || '').toLowerCase()
  .replace(/&/g, ' and ').replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');

// As backend/kitchen.py `BAR_CATEGORIES`: sections the bar makes, which never reach the kitchen.
const BAR = new Set([
  'lagers', 'wines', 'cocktails', 'soft_drinks', 'drinks', 'kids_drinks', 'shakes', 'beverages',
  'wine', 'beer', 'beers', 'ales', 'cider', 'ciders', 'beer_and_cider', 'beers_and_ciders',
  'spirits', 'mocktails', 'juices', 'hot_drinks', 'coffee', 'coffees', 'tea', 'teas',
]);
export const isBarSection = (category) => BAR.has(categoryKey(category));

/** The pass's key on a ticket line. */
export const PASS = 'kitchen';

/** `{section key: [station keys]}`: what was set, and for a section that wasn't, the first
 *  station that claims it the old way (`categories`), as a one-stop route. */
export const sectionRoutes = (stations = [], routes = {}) => {
  const out = {};
  (stations || []).forEach((st) => (st.categories || []).forEach((c) => {
    const k = categoryKey(c);
    if (!(k in out)) out[k] = [st.key];
  }));
  Object.entries(routes || {}).forEach(([section, route]) => { out[categoryKey(section)] = [...(route || [])]; });
  return out;
};

const runnable = (route, stations) => {
  const known = new Set((stations || []).map((s) => s.key));
  return [...new Set((route || []).filter((k) => known.has(k)))];
};

/** The route a dish takes: its own, else its old single station, else its section's. */
export const routeFor = ({ category, kitchen_route: own, kitchen_station: one } = {}, stations = [], routes = {}) => {
  const mine = runnable(own, stations);
  if (mine.length) return mine;
  if (one && (stations || []).some((s) => s.key === one)) return [one];
  return runnable(sectionRoutes(stations, routes)[categoryKey(category)], stations);
};

/** Every station a ticket line goes through: its route, or its one station before routes. */
export const lineRoute = (line) => (line?.route?.length ? line.route : [line?.station || PASS]);

/** Does `station` work on this line? */
export const onStation = (line, station) => lineRoute(line).includes(station);

/** "Chicken, Fry, Build": a route in words, with the stations' names. */
export const routeWords = (route, stations = []) => (route || [])
  .map((k) => (stations || []).find((s) => s.key === k)?.name || k).join(', ');

/** A new station's key from its name, unique among `taken`. */
export const stationKey = (name, taken = []) => {
  const base = String(name || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 28) || 'station';
  const reserved = new Set(['kitchen', 'pass', 'bar', 'all', ...taken]);
  if (!reserved.has(base)) return base;
  for (let i = 2; ; i += 1) if (!reserved.has(`${base}-${i}`)) return `${base}-${i}`;
};
